/**
 * The short, readable label for a record id: `VD-005`, `QT-90F0D6B4`.
 *
 * Lists show this in their first column and the detail URL carries the same
 * id behind the same prefix (see entityRoutes.ts), so what a person reads out
 * of a table is what they find in the address bar.
 *
 * The id is opaque. It is a plain number on rows that are still integers and a
 * uuid on rows converted to Kalligo-shaped tables, so nothing here calls
 * Number() on it: a number-looking id is zero-padded, a uuid is shortened to
 * its first 8 characters (enough to tell records apart on screen; the URL
 * keeps the whole id).
 */
import { fmtId } from './jiraId';
import { ENTITY_PREFIX, type EntityKind } from './entityRoutes';
import { getRecordRef } from './recordRefs';

const DIGITS_ONLY = /^\d+$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_PLAIN_LENGTH = 12;

/** The part of the label after the prefix. */
function shortId(raw: string): string {
  if (DIGITS_ONLY.test(raw)) return raw.padStart(3, '0');
  if (UUID.test(raw)) return raw.slice(0, 8).toUpperCase();
  return raw.length > MAX_PLAIN_LENGTH ? raw.slice(0, 8).toUpperCase() : raw;
}

/** Label for a record, or an em dash when it has no id yet (an unsaved row). */
export function recordId(prefix: string, id: string | number | null | undefined): string {
  if (id === null || id === undefined || id === '') return '—';
  // Rows from some endpoints already carry the prefix ("GS-033").
  const full = fmtId(prefix, id);
  const raw = full.slice(prefix.length + 1);
  return `${prefix.toUpperCase()}-${shortId(raw)}`;
}

/** Readable reference for an id shown on its own: a uuid becomes its first 8 characters, anything else is kept. */
export function shortRef(id: string | number | null | undefined): string {
  if (id === null || id === undefined) return '';
  const raw = String(id);
  return UUID.test(raw) ? raw.slice(0, 8).toUpperCase() : raw;
}

/** A record number as shown: zero-padded to 3 digits, unpadded above 999. */
function numberLabel(prefix: string, n: number): string {
  return `${prefix.toUpperCase()}-${String(n).padStart(3, '0')}`;
}

/**
 * The record's number as a label (`PO-130`), or undefined when the API has not
 * given it one. Reads the store at call time, so a component calling this must
 * also subscribe with useRecordRefs() to re-render when the numbers load.
 */
export function recordNumberLabel(kind: EntityKind, id: string | number | null | undefined): string | undefined {
  if (id === null || id === undefined || id === '') return undefined;
  const prefix = ENTITY_PREFIX[kind];
  // Rows from some endpoints already carry the prefix ("PO-<uuid>").
  const text = String(id);
  const raw = text.toUpperCase().startsWith(`${prefix.toUpperCase()}-`) ? text.slice(prefix.length + 1) : text;
  const n = getRecordRef(kind, raw);
  return n === undefined ? undefined : numberLabel(prefix, n);
}

/**
 * The label to show for a record: its number (`PO-130`) when known, otherwise
 * exactly what recordId shows (`PO-90F0D6B4`).
 */
export function recordLabel(kind: EntityKind, id: string | number | null | undefined): string {
  return recordNumberLabel(kind, id) ?? recordId(ENTITY_PREFIX[kind], id);
}

/**
 * Like recordLabel, for a place that shows something other than the short
 * label when a record has no number (a full id, a bare fragment): that text is
 * passed in as `fallback` and shown unchanged.
 */
export function recordLabelOr(kind: EntityKind, id: string | number | null | undefined, fallback: string): string {
  return recordNumberLabel(kind, id) ?? fallback;
}
